import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";
import * as api from "../api";
import { ApiError } from "../api";
import { Chip, Ic } from "../components/ui";
import { useSession } from "../session";
import { useFormat, useT } from "../i18n";
import type { Cheie } from "../i18n";

const INVITE_ERRORS: Record<string, Cheie> = {
  invalid_email: "org.email_invalid",
  already_member: "org.deja_membru",
  already_invited: "org.deja_invitat",
  forbidden: "org.fara_drept_invitare",
};

const MEMBER_ERRORS: Record<string, Cheie> = {
  cannot_disable_self: "org.nu_te_dezactivezi",
  forbidden: "org.doar_owner",
};

/** Starea unei invitații, ca (cheie de text, ton de pastilă). Perechea stă
 *  într-un loc: ecranul întreabă o dată «în ce stare e» și primește și cum se
 *  scrie, și cum se colorează. */
const STARI = {
  acceptata: { cheie: "org.stare_acceptata", ton: "ok" },
  expirata: { cheie: "org.stare_expirata", ton: "amb" },
  deschisa: { cheie: "org.stare_deschisa", ton: "mut" },
} as const;

export default function Organization() {
  const { user } = useSession();
  const t = useT();
  const { data } = useFormat();
  const organizations = user?.organizations ?? [];
  const [orgId, setOrgId] = useState(organizations[0]?.id ?? "");
  const membership = organizations.find((org) => org.id === orgId);
  const canManage = membership?.role === "owner" || membership?.role === "admin";

  const [tab, setTab] = useState<"members" | "invitations">("members");
  const [members, setMembers] = useState<api.Member[]>([]);
  const [invitations, setInvitations] = useState<api.Invitation[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<api.OrgRole>("member");
  const [freshLink, setFreshLink] = useState("");
  const [error, setError] = useState("");

  const reload = useCallback(() => {
    if (!orgId) return;
    api.listMembers(orgId).then(setMembers).catch(() => undefined);
    if (canManage) api.listInvitations(orgId).then(setInvitations).catch(() => undefined);
  }, [orgId, canManage]);

  useEffect(reload, [reload]);

  async function invite(event: FormEvent) {
    event.preventDefault();
    setError("");
    setFreshLink("");
    try {
      const invitation = await api.createInvitation(orgId, email.trim(), role);
      setInvitations((current) => [invitation, ...current]);
      setEmail("");
      // Linkul se poate arăta o SINGURĂ dată: baza ține doar hash-ul tokenului.
      if (invitation.link) setFreshLink(invitation.link);
    } catch (err) {
      setError(t(err instanceof ApiError
        ? INVITE_ERRORS[err.code] ?? "org.esec_invitatie"
        : "org.esec_invitatie"));
    }
  }

  async function remove(invitation: api.Invitation) {
    if (!confirm(t("org.confirma_stergere_invitatie",
                   { email: invitation.email }))) return;
    try {
      await api.deleteInvitation(orgId, invitation.id);
      setInvitations((current) => current.filter((i) => i.id !== invitation.id));
      if (freshLink.endsWith(invitation.id)) setFreshLink("");
    } catch {
      setError(t("org.esec_stergere_invitatie"));
    }
  }

  async function toggle(member: api.Member) {
    if (!confirm(t(member.disabled ? "org.confirma_reactivare"
                                   : "org.confirma_dezactivare",
                   { email: member.email }))) return;
    setError("");
    try {
      const updated = member.disabled
        ? await api.enableMember(orgId, member.user_id)
        : await api.disableMember(orgId, member.user_id);
      setMembers((current) =>
        current.map((m) => (m.user_id === updated.user_id ? updated : m)));
    } catch (err) {
      setError(t(err instanceof ApiError
        ? MEMBER_ERRORS[err.code] ?? "org.esec_stare_cont"
        : "org.esec_stare_cont"));
    }
  }

  const state = (invitation: api.Invitation) =>
    invitation.accepted_at ? STARI.acceptata
      : new Date(invitation.expires_at) < new Date() ? STARI.expirata
      : STARI.deschisa;

  return (
    <div className="page">
      <div className="page-head">
        <div className="disp">{t("org.titlu")}</div>
        {organizations.length > 1 && (
          <select value={orgId} style={{ width: "auto" }}
                  onChange={(e) => setOrgId(e.target.value)}>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
        )}
        <div className="tabs">
          <button className={tab === "members" ? "on" : ""} onClick={() => setTab("members")}>
            {t("org.membri")}
          </button>
          {canManage && (
            <button className={tab === "invitations" ? "on" : ""}
                    onClick={() => setTab("invitations")}>
              {t("org.invitatii")}
            </button>
          )}
        </div>
      </div>

      {error && <p className="note bad" style={{ marginBottom: 12 }}>{error}</p>}

      {tab === "members" && (
        <ul className="rows">
          {members.map((member) => (
            <li key={member.user_id} className={`row ${member.disabled ? "dim" : ""}`}>
              <span className="av">{initials(member)}</span>
              <div>
                <div className="name">{member.full_name || member.email}</div>
                <div className="meta">{member.email}</div>
              </div>
              <div className="sp" />
              {member.disabled && <Chip tone="bad">{t("org.dezactivat")}</Chip>}
              <Chip tone={member.role === "owner" ? "acc" : "mut"}>{member.role}</Chip>
              {canManage && member.user_id !== user?.id && (
                <button className="btn btng" onClick={() => toggle(member)}>
                  {t(member.disabled ? "org.reactiveaza" : "org.dezactiveaza")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {tab === "invitations" && canManage && (
        <>
          <form className="row-form" onSubmit={invite}>
            <input type="email" placeholder="email@exemplu.ro" value={email} required
                   onChange={(e) => setEmail(e.target.value)} />
            <select value={role} onChange={(e) => setRole(e.target.value as api.OrgRole)}>
              <option value="member">{t("org.rol_membru")}</option>
              <option value="admin">{t("org.rol_admin")}</option>
              <option value="owner">{t("org.rol_owner")}</option>
            </select>
            <button className="btn btnp" type="submit"><Ic n="plus" s={14} />{t("org.invita")}</button>
          </form>

          {freshLink && (
            <div className="callout">
              <b>{t("org.link_titlu")}</b> {t("org.link_text")}
              <code>{freshLink}</code>
            </div>
          )}

          {invitations.length === 0 && <p className="note">{t("org.nicio_invitatie")}</p>}

          <ul className="rows">
            {invitations.map((invitation) => (
              <li key={invitation.id} className="row">
                <div>
                  <div className="name">{invitation.email}</div>
                  <div className="meta">
                    {invitation.role} · {t("org.expira")}{" "}
                    {data(invitation.expires_at)}
                  </div>
                </div>
                <div className="sp" />
                <Chip tone={state(invitation).ton}>
                  {t(state(invitation).cheie)}
                </Chip>
                {state(invitation) !== STARI.acceptata && (
                  <button className="btn btng" onClick={() => remove(invitation)}>
                    <Ic n="x" s={13} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function initials(member: api.Member): string {
  const source = member.full_name || member.email;
  return source
    .split(/[\s.@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}
